import { Alert, Button, LogInIcon } from "@strust/ui";
import { css } from "@strust/ui/css";
import type { SyntheticEvent } from "react";
import { useIntl } from "react-intl";
import { resetFields, submitFields } from "../../../shared/form-field.ts";
import { TotpCodeField, useTotpCodeField } from "../../../shared/totp-code-field.tsx";
import { TotpSetup, type TotpSetupProps } from "../../../shared/totp-setup.tsx";
import { loginMessages, loginStepMessages } from "../messages.ts";
import { AuthHeading, AuthLayout, AuthNote } from "./auth-layout.tsx";
import { InvitationForm } from "./invitation-form.tsx";

const FORM = css({ display: "flex", flexDirection: "column", gap: "4" });
const FULL = css({ width: "full" });

export type LoginStepScreenProps =
  | {
      /** The code of the user's authenticator (`202 mfa_required`). */
      readonly step: "mfa_required";
      readonly onSubmitCode: (code: string) => void;
      readonly pending: boolean;
      readonly error?: string | undefined;
      readonly onRestart: () => void;
    }
  | {
      /**
       * The new password replacing the temporary one of a first login (`202
       * new_password_required`, change adopt-installation-per-customer).
       */
      readonly step: "new_password_required";
      readonly onSubmitPassword: (password: string) => void;
      readonly pending: boolean;
      readonly error?: string | undefined;
      /** The server refused the last password as common or breached. */
      readonly commonRefused?: boolean;
      readonly onRestart: () => void;
    }
  | ({
      /** The mandatory set-up of an authenticator (`202 mfa_setup_required`). */
      readonly step: "mfa_setup_required";
      /** The set-up of a two-factor recovery invitation (new password already set). */
      readonly recovery?: boolean;
      readonly onRestart: () => void;
    } & Omit<TotpSetupProps, "secondary">);

/**
 * The step a right password still needs before a session starts (design D5): the code of the
 * authenticator, the new password replacing a temporary one, or the mandatory set-up of an
 * authenticator. No other screen is reachable meanwhile: there is no session yet. Presentational.
 */
export function LoginStepScreen(props: LoginStepScreenProps) {
  const intl = useIntl();
  const restart = {
    label: intl.formatMessage(loginStepMessages.restart),
    onClick: props.onRestart,
  };
  return (
    <AuthLayout busy={props.pending}>
      {props.step === "mfa_required" ? (
        <>
          <AuthHeading
            title={intl.formatMessage(loginStepMessages.codeTitle)}
            subtitle={intl.formatMessage(loginStepMessages.codeSubtitle)}
          />
          <CodeForm {...props} />
        </>
      ) : props.step === "new_password_required" ? (
        <>
          <AuthHeading
            title={intl.formatMessage(loginStepMessages.newPasswordTitle)}
            subtitle={intl.formatMessage(loginStepMessages.newPasswordSubtitle)}
          />
          <InvitationForm
            onSubmit={props.onSubmitPassword}
            pending={props.pending}
            error={props.error}
            commonRefused={props.commonRefused === true}
            submitLabel={intl.formatMessage(loginStepMessages.newPasswordSubmit)}
          />
          <Button
            visual="ghost"
            className={FULL}
            onClick={props.onRestart}
            disabled={props.pending}
          >
            {restart.label}
          </Button>
        </>
      ) : (
        <>
          <AuthHeading
            title={intl.formatMessage(loginStepMessages.setupTitle)}
            subtitle={intl.formatMessage(
              props.recovery === true
                ? loginStepMessages.recoverySubtitle
                : loginStepMessages.setupSubtitle,
            )}
          />
          <TotpSetup
            setup={props.setup}
            onConfirm={props.onConfirm}
            pending={props.pending}
            error={props.error}
            secondary={restart}
          />
        </>
      )}
      <AuthNote>{intl.formatMessage(loginMessages.auditNote)}</AuthNote>
    </AuthLayout>
  );
}

function CodeForm({
  onSubmitCode,
  pending,
  error,
  onRestart,
}: Extract<LoginStepScreenProps, { step: "mfa_required" }>) {
  const intl = useIntl();
  const code = useTotpCodeField();
  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    if (!submitFields(code)) return;
    onSubmitCode(code.value);
    resetFields(code);
  };
  return (
    <form className={FORM} onSubmit={submit} noValidate>
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <TotpCodeField field={code} autoFocus />
      <Button
        type="submit"
        visual="primary"
        className={FULL}
        icon={<LogInIcon />}
        loading={pending}
      >
        {intl.formatMessage(loginStepMessages.verify)}
      </Button>
      <Button visual="ghost" className={FULL} onClick={onRestart} disabled={pending}>
        {intl.formatMessage(loginStepMessages.restart)}
      </Button>
    </form>
  );
}
